import React, { useState } from "react";
import { Box, Button, TextField, Switch, Typography, FormControl, InputLabel, Select, MenuItem, CircularProgress } from "@mui/material";

const ExchangeEditForm = ({ exchange, onSave, onCancel, isSaving = false }) => {
  const [position, setPosition] = useState(exchange.position || 0);
  const [currencies, setCurrencies] = useState(exchange.currencies || []);
  const [active, setActive] = useState(exchange.active);
  // KYC / AML routing
  const [kycEnabled, setKycEnabled] = useState(!!exchange.kyc_identity_enabled);
  const [providerCode, setProviderCode] = useState(exchange.kyc_provider_code || "");
  const [providerAccountKey, setProviderAccountKey] = useState(
    exchange.kyc_provider_account_key || ""
  );
  const [threshold, setThreshold] = useState(
    exchange.kyc_identity_threshold_usd != null ? exchange.kyc_identity_threshold_usd : ""
  );
  const [amlEnabled, setAmlEnabled] = useState(!!exchange.aml);

  const handleSave = () => {
    if (isSaving) return;
    const updated = {
      ...exchange,
      position,
      currencies,
      active,
      kyc_identity_enabled: kycEnabled,
      kyc_provider_code: providerCode || null,
      kyc_provider_account_key: providerAccountKey || null,
      kyc_identity_threshold_usd:
        threshold === "" || threshold === null ? null : Number(threshold),
      aml: amlEnabled,
    };
    onSave(updated);
  };

  const currenciesOptions = [
    { value: "USD", label: "USD" },
    { value: "MXN", label: "MXN" },
    { value: "COP", label: "COP" },
    { value: "PEN", label: "PEN" },
    { value: "BRL", label: "BRL" },
    { value: "CLP", label: "CLP" },
    { value: "ARS", label: "ARS" },
    { value: "BOB", label: "BOB" },
    { value: "VES", label: "VES" },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Exchange Edit
      </Typography>

      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <TextField
          label="Code"
          value={exchange.code}
          InputProps={{ readOnly: true, disabled: true }}
          size="small"
          sx={{ maxWidth: 120 }}
        />
        <TextField
          label="Name"
          value={exchange.name}
          InputProps={{ readOnly: true, disabled: true }}
          size="small"
          sx={{ maxWidth: 170 }}
        />
        <TextField
          label="Category"
          value={exchange.category}
          InputProps={{ readOnly: true, disabled: true }}
          size="small"
          sx={{ maxWidth: 170 }}
        />
        <TextField
          label="Position"
          type="number"
          value={position}
          InputProps={{ readOnly: true, disabled: true }}
          onChange={e => setPosition(Number(e.target.value))}
          size="small"
          sx={{ width: 100 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={isSaving}>
          <InputLabel>Currencies</InputLabel>
          <Select
            multiple
            value={currencies}
            onChange={e => setCurrencies(e.target.value)}
            label="Currencies"
            renderValue={(selected) => selected.join(', ')}
            disabled={isSaving}
          >
            {currenciesOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
          </Select>
        </FormControl>
        <Box display="flex" alignItems="center">
          <Typography sx={{ mr: 1 }}>Status</Typography>
          <Switch
            checked={active}
            onChange={e => setActive(e.target.checked)}
            color="primary"
            size="small"
            disabled={isSaving}
          />
          <Typography sx={{ ml: 1 }}>{active ? "E" : "D"}</Typography>
        </Box>
      </Box>

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        KYC / AML
      </Typography>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <Box display="flex" alignItems="center">
          <Typography sx={{ mr: 1 }}>Identity KYC</Typography>
          <Switch
            checked={kycEnabled}
            onChange={e => setKycEnabled(e.target.checked)}
            color="primary"
            size="small"
            disabled={isSaving}
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>KYC Provider</InputLabel>
          <Select
            value={providerCode}
            onChange={e => setProviderCode(e.target.value)}
            label="KYC Provider"
            disabled={!kycEnabled || isSaving}
          >
            <MenuItem value="">—</MenuItem>
            <MenuItem value="TRIA">TRIA</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Provider Account"
          value={providerAccountKey}
          onChange={e => setProviderAccountKey(e.target.value)}
          disabled={!kycEnabled || isSaving}
          size="small"
          sx={{ width: 160 }}
          placeholder="TRIA_MX"
        />
        <TextField
          label="Threshold USD"
          type="number"
          value={threshold}
          onChange={e => setThreshold(e.target.value)}
          disabled={!kycEnabled || isSaving}
          size="small"
          sx={{ width: 140 }}
          helperText="Override (blank = default)"
        />
        <Box display="flex" alignItems="center">
          <Typography sx={{ mr: 1 }}>Wallet AML</Typography>
          <Switch
            checked={amlEnabled}
            onChange={e => setAmlEnabled(e.target.checked)}
            color="primary"
            size="small"
            disabled={isSaving}
          />
        </Box>
      </Box>

      <Box display="flex" gap={2} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
        <Button variant="outlined" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default ExchangeEditForm;
